import React, { useEffect, useState } from "react";
import { Loader2, BadgeCheck, RotateCcw, MessageSquareText } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { toast } from "../../../components/ui/sonner";
import { fmtT, Progress } from "./meta";
import { T } from "../../../lib/i18n";
import { errTextOf } from "../../../lib/detailText";
// 【2026-10-26】撤销结项改用自建确认弹窗，不再用 window.confirm（原因见组件内注释）
import { DesignConfirmDialog } from "./DesignConfirmDialog";

/* 已结项（设计完成，不进工程部）—— 2026-10-20 用户需求「有的项目不需要移交施工，设计完了就直接结束」。
 *
 * 为什么单独一个列表、而不是塞进「已移交工程部」：
 *   移交列表的列是「工程项目 / PM / 开工 / 移交回执」，结项项目这些字段**全都不存在**，
 *   混在一张表里只会整列红字「未关联工程项目」，看起来像数据出错。
 *   这里改成「完成时间 / 确认人 / 结项原因 / 未完成产物」，讲的是另一件事。
 *
 * 与「已取消」的区别：结项是设计正常交付完成（客户拿到图纸），取消是合同中途终止。
 * 因此这里不做「CRM 线索标记流失」等取消联动，只读归档。
 */
export const CompletedList = ({ api, meta, branch, q, onOpen, onChanged }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  /* 【2026-10-26】撤销结项改用自建确认弹窗。
     修改原因：原先用 window.confirm，在不支持原生弹窗的宿主（如内置浏览器）里
     会抛异常并糊一层红色报错，列表页的用户完全无法撤销结项；原生 confirm 也
     说不清「撤销后会重新开始催办」这个后果。现在先记录待撤销的项目，由弹窗确认后再提交。
     最终效果：`reopen(p)` 拆成「打开弹窗（记 p）」与「真正提交（doReopen）」，
     接口、权限、刷新口径全部不变。 */
  const [reopenTarget, setReopenTarget] = useState(null);
  useEffect(() => {
    setD(null);
    api.get("/design/v3/projects", { params: { closed: 1, branch, q: q || undefined } })
      .then(({ data }) => setD(data))
      .catch((e) => setD({ projects: [], error: e?.detailText || e.message }));
  }, [api, branch, q]);
  const doReopen = async () => {
    const p = reopenTarget;
    if (!p) return;
    setBusy(p.id);
    try {
      const { data } = await api.post(`/design/v3/projects/${p.id}/reopen`);
      toast.success(T("已撤销结项，恢复为「{0}」", [T(data.workStatusName || data.workStatus || "")]));
      const { data: nd } = await api.get("/design/v3/projects", { params: { closed: 1, branch, q: q || undefined } });
      setD(nd);
      // 撤销结项后项目回到「进行中」：父列表的 doneN / activeN 也要重拉，
      // 否则下拉里的「已结项（1）」会一直是旧数字（本组件只能刷新自己的表格）。
      onChanged?.();
      setReopenTarget(null);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy("");
  };
  return (
    <div className="space-y-3" data-testid="dv3-completed">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-semibold text-teal-800 inline-flex items-center gap-1" data-testid="dv3-completed-note">
          <BadgeCheck className="h-4 w-4" />{T("设计已完成 · 不进工程部的项目（只做设计 / 客户自行施工）· 设计进度按 100% 计")}
        </span>
      </div>
      {!d ? <div className="h-32 flex items-center justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        : d.error ? <p className="text-sm text-red-600" data-testid="dv3-completed-error">{errTextOf(d.error)}</p>
        : d.projects.length === 0 ? <div className="p-10 text-center text-sm text-slate-400 bg-white rounded-lg border border-slate-200" data-testid="dv3-completed-empty">{T("还没有已结项的项目")}</div> : (
          <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto" data-testid="dv3-completed-table">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-[10px] text-slate-400 border-b border-slate-200 bg-slate-50">
                <th className="py-2 px-3">{T("项目")}</th>
                <th className="py-2 px-3">{T("分公司")}</th>
                <th className="py-2 px-3 w-36">{T("设计进度")}</th>
                <th className="py-2 px-3">{T("结项时间 / 确认人")}</th>
                <th className="py-2 px-3">{T("未完成产物")}</th>
                <th className="py-2 px-3">{T("结项原因")}</th>
                <th className="py-2 px-3 w-24" />
              </tr></thead>
              <tbody>{d.projects.map((p) => {
                // 后端把「结项时仍未完成」的产物名记在 completeReason 之外的活动里；
                // 列表只按当前产物状态实时算，避免和结项瞬间的快照说法不一致。
                const todo = (p.disciplines || []).filter((x) => !["approved", "done", "exempt"].includes(x.status));
                return (
                  <tr key={p.id} data-testid={`dv3-completed-row-${p.id}`} data-incomplete={todo.length}
                    onClick={() => onOpen(p.id)} className="border-b border-slate-100 last:border-0 hover:bg-teal-50/40 cursor-pointer">
                    <td className="py-2 px-3"><p className="font-semibold text-slate-800">{p.address}</p><p className="text-[10px] text-slate-400">{p.city} · {p.projectId}{p.ownerName ? ` · ${p.ownerName}` : ""}</p></td>
                    <td className="py-2 px-3 text-slate-600">{T(meta.branches[p.branch]?.name)}</td>
                    <td className="py-2 px-3"><div className="flex items-center gap-2"><Progress v={100} color="#0f766e" /><b className="w-10 text-right text-teal-700" data-testid={`dv3-completed-progress-${p.id}`}>100%</b></div></td>
                    <td className="py-2 px-3 text-slate-700"><p>{fmtT(p.completedAt)}</p><p className="text-[10px] text-slate-400">{p.completedBy || "—"}</p></td>
                    <td className="py-2 px-3">
                      {todo.length === 0
                        ? <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5" data-testid={`dv3-completed-all-${p.id}`}><BadgeCheck className="h-3 w-3" />{T("全部完成")}</span>
                        : <div className="flex flex-wrap gap-1" data-testid={`dv3-completed-incomplete-${p.id}`}>
                          <span className="text-[10px] font-bold text-amber-800 bg-amber-100 rounded px-1.5 py-0.5">{todo.length}</span>
                          {todo.slice(0, 3).map((x) => <span key={x.key} className="text-[10px] text-slate-500 border border-slate-200 rounded px-1">{T(x.zh || x.name || x.key)}</span>)}
                          {todo.length > 3 && <span className="text-[10px] text-slate-400">+{todo.length - 3}</span>}
                        </div>}
                    </td>
                    <td className="py-2 px-3 text-slate-700 max-w-[260px]"><p className="truncate" title={p.completeReason}>{p.completeReason || "—"}</p></td>
                    <td className="py-2 px-3 text-right">
                      {meta.isManager && (
                        <Button size="sm" variant="outline" data-testid={`dv3-reopen-${p.id}`} disabled={busy === p.id}
                          onClick={(e) => { e.stopPropagation(); setReopenTarget(p); }} className="h-7 gap-1 border-teal-200 text-teal-700 hover:bg-teal-50">
                          {busy === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}{T("撤销结项")}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        )}
      <p className="text-[11px] text-slate-400 flex items-center gap-1"><MessageSquareText className="h-3 w-3" />{T("结项项目的产物、附件与评论全部保留（只读），需要继续跟进时点「撤销结项」即可回到进行中。")}</p>
      {/* 撤销结项确认（2026-10-26 换自建弹窗）：红色危险语义，讲清「会重新开始催办」 */}
      {reopenTarget && (
        <DesignConfirmDialog
          testId="dv3-list-reopen"
          tone="danger"
          title={T("撤销结项")}
          subtitle={reopenTarget.address}
          message={T("撤销后项目回到结项前的设计状态，并重新进入「进行中」列表；逾期 / 停滞 / 节点提醒会重新开始催办。")}
          warning={T("这一步不影响已上传的产物与附件，但会让项目重新计入进度与逾期统计。")}
          confirmText={T("确认撤销结项")}
          busy={busy === reopenTarget.id}
          onCancel={() => setReopenTarget(null)}
          onConfirm={doReopen}
        />
      )}
    </div>
  );
};
